import { cloneElement, isValidElement, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { useWhoami } from "@/hooks/useWhoami";
import { permissionHint } from "@/lib/strings";
import type { Permission } from "@/types/fleet";

type Mode = "hide" | "disable";

/**
 * Gate a control on a permission from `GET /api/v1/auth/me`.
 *
 * **Fails closed**: while the identity is unknown — first paint, a network
 * blip, an expired session — nothing is permitted. The fork's version defaulted
 * an unknown role to admin so the UI "stayed usable"; what it actually did was
 * show a viewer a console full of live-looking buttons that 403 on click, and
 * during an outage it did that to everybody simultaneously.
 *
 * This is still not the security boundary — the server enforces the real check
 * on every write. It is about not lying to the operator.
 *
 * Two modes:
 *   - "disable" (default) keeps the control visible but inert, with a tooltip
 *     naming the role required. The operator learns the action exists and why
 *     they cannot use it.
 *   - "hide" renders `fallback`. For whole tabs and panels a viewer has no
 *     business seeing at all.
 */
export function Guarded({
  permission,
  mode = "disable",
  children,
  fallback = null,
}: {
  permission: Permission;
  mode?: Mode;
  children: ReactElement;
  fallback?: ReactNode;
}) {
  const { hasPermission } = useWhoami();
  if (hasPermission(permission)) return children;

  if (mode === "hide") return <>{fallback}</>;
  if (!isValidElement(children)) return children;

  const props = children.props as Record<string, unknown>;
  const existingTitle = typeof props.title === "string" ? props.title : undefined;

  return cloneElement(children as ReactElement<Record<string, unknown>>, {
    disabled: true,
    title: existingTitle ?? `需要權限：${permissionHint(permission)}`,
    "aria-disabled": true,
    onClick: undefined,
    style: {
      ...((props.style as CSSProperties | undefined) ?? {}),
      opacity: 0.55,
      cursor: "not-allowed",
    },
  });
}

/** Imperative form, for logic that is not a single element. */
export function useCan(permission: Permission): boolean {
  return useWhoami().hasPermission(permission);
}
